import React, { useEffect, useState } from 'react';
import { Grid, TextField, Autocomplete, Button, Typography, Box } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import styles from './ConfigForm.styles';
import ValidatedField from '../../../../components/inputs/ValidatedField';
import {
  OPERATOR_RULES,
  UNIT_RULES,
  EVN_RULES,
  METER_NUMBER_RULES,
  FROM_DATE_RULES,
  COMMENTS_RULES,
} from '../../utils/configFormValidation';

const filter = createFilterOptions();

const FieldLabel = ({ children }) => (
  <Typography variant="subtitle2" sx={styles.labelText}>
    {children}
  </Typography>
);

const isEmpty = (v) => !String(v ?? '').trim();

const ConfigForm = ({
  operatorOptions,
  unitOptions,
  evnOptions,
  meterTypeOptions,
  operator,
  unit,
  evn,
  meterType,
  meterNumber,
  fromDate,
  comments,
  formErrors = {},
  setOperator,
  setUnit,
  setEvn,
  setMeterType,
  setMeterNumber,
  setFromDate,
  setComments,
  handleAdd,
  handleClear,
  editing,
  onSave,
  onCancel,
  formKey,              
  hasOperator,
  isExistingUnit,
  submitted = false,
}) => {
  const unitDisabled = !hasOperator;
  const evnDisabled = !unit;
  const meterDisabled = !evn;

  const [unitInput, setUnitInput] = useState(unit || '');
  const [evnInput, setEvnInput] = useState(evn || '');
  const [meterInput, setMeterInput] = useState(meterNumber || '');

  useEffect(() => { setUnitInput(unit || ''); }, [unit]);
  useEffect(() => { setEvnInput(evn || ''); }, [evn]);
  useEffect(() => { setMeterInput(meterNumber || ''); }, [meterNumber]);

  const commitUnit = (val) => {
    const v = (val ?? '').trim();
    if (v !== (unit || '')) setUnit(v || null);
  };
  const commitEvn = (val) => {
    const v = (val ?? '').trim();
    if (v !== (evn || '')) setEvn(v || null);
  };

  const meterTypeDisabled = isEmpty(meterNumber);

  const validOperator = OPERATOR_RULES.every((r) => r.test(operator || ''));
  const validUnit     = !unitDisabled && UNIT_RULES.every((r) => r.test(unitInput));
  const validEvn      = !evnDisabled && EVN_RULES.every((r) => r.test(evn || ''));
  const validMeter    = !meterDisabled && METER_NUMBER_RULES.every((r) => r.test(meterNumber || ''));
  const validFromDate = FROM_DATE_RULES.every((r) => r.test(fromDate || ''));
  const validComments = COMMENTS_RULES.every((r) => r.test(comments || ''));
  const validMeterType = !meterTypeDisabled && Boolean(meterType);

  const canSubmit =
    validOperator &&
    validUnit &&
    validEvn &&
    validMeter &&
    validMeterType &&
    validFromDate &&
    validComments;

  return (
    <Grid container rowSpacing={1} columnSpacing={2} sx={{ direction: 'ltr' }}>
      {/* Operator (code) */}
      <Grid item xs={12} sm={6} md={3}>
        <FieldLabel>Operator</FieldLabel>
        <Box sx={styles.fieldInput}>
          <Autocomplete
            options={operatorOptions}
            value={operator || ''}
            onChange={(_, v) => setOperator(v || null)}
            isOptionEqualToValue={(o, v) => o === v}
            getOptionLabel={(o) => (typeof o === 'string' ? o : '')}
            renderInput={(params) => {
              const operatorError = OPERATOR_RULES.find((r) => (operator || '') !== '' && !r.test(operator || ''));
              const externalOperatorError = formErrors.operator || '';
              const operatorErrorMsg = externalOperatorError || (operatorError ? operatorError.message : '');
              const showRequired = submitted && !operator;
              return (
                <TextField
                  {...params}
                  placeholder="Select operator"
                  aria-label="Operator"
                  size="small"
                  variant="outlined"
                  fullWidth
                  required
                  error={!!operatorErrorMsg || showRequired}
                  helperText={operatorErrorMsg || (showRequired ? 'Operator is required' : '')}
                  sx={styles.errorOutlined}
                />
              );
            }}
          />
        </Box>
      </Grid>

      {/* Unit */}
      <Grid item xs={12} sm={6} md={3}>
        <FieldLabel>Unit</FieldLabel>
        <Box sx={styles.fieldInput}>
          <Autocomplete
            freeSolo
            filterOptions={(options, params) => filter(options, params)}
            options={unitDisabled ? [] : unitOptions}
            value={unit || ''}
            inputValue={unitInput}
            onInputChange={(_, v) => {
              setUnitInput(v || '');
              // commit immediately so dependent fields enable while tabbing
              commitUnit(v || '');
            }}
            onChange={(_, v) => {
              const next = (typeof v === 'string' ? v : '') || '';
              setUnitInput(next);
              commitUnit(next);
            }}
            onBlur={() => commitUnit(unitInput)}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
            disabled={unitDisabled}
            renderInput={(params) => {
              const unitError = UNIT_RULES.find((r) => !unitDisabled && unitInput !== '' && !r.test(unitInput));
              const externalUnitError = formErrors.unit || '';
              const unitErrorMsg = externalUnitError || (unitError ? unitError.message : '');
              const showRequired = submitted && !unitDisabled && isEmpty(unitInput);
              return (
                <TextField
                  {...params}
                  placeholder={unitDisabled ? 'Select operator first' : 'Enter or select unit'}
                  aria-label="Unit"
                  size="small"
                  variant="outlined"
                  fullWidth
                  required={!unitDisabled}
                  error={!!unitErrorMsg || showRequired}
                  helperText={unitErrorMsg || (showRequired ? 'Unit is required' : '')}
                  sx={styles.errorOutlined}
                />
              );
            }}
          />
        </Box>
      </Grid>

      {/* EVN */}
      <Grid item xs={12} sm={6} md={3}>
        <FieldLabel>EVN</FieldLabel>
        <Box sx={styles.fieldInput}>
          <Autocomplete
            freeSolo
            filterOptions={(options, params) => filter(options, params)}
            options={evnDisabled ? [] : (isExistingUnit ? evnOptions : [])}
            value={evn || ''}
            inputValue={evnInput}
            onInputChange={(_, v) => {
              setEvnInput(v || '');
              // commit immediately so dependent fields enable while tabbing
              commitEvn(v || '');
            }}
            onChange={(_, v) => {
              const next = (typeof v === 'string' ? v : '') || '';
              setEvnInput(next);
              commitEvn(next);
            }}
            onBlur={() => commitEvn(evnInput)}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
            disabled={evnDisabled}
            renderInput={(params) => {
              const evnError = EVN_RULES.find((r) => !evnDisabled && (evn || '') !== '' && !r.test(evn || ''));
              const externalEvnError = formErrors.evn || '';
              const evnErrorMsg = externalEvnError || (evnError ? evnError.message : '');
              const showRequired = submitted && !evnDisabled && isEmpty(evn);
              return (
                <TextField
                  {...params}
                  placeholder={
                    evnDisabled
                      ? 'Enter or select unit first'
                      : (isExistingUnit ? 'Enter or select EVN' : 'Enter a new EVN')
                  }
                  aria-label="EVN"
                  size="small"
                  variant="outlined"
                  fullWidth
                  required={!evnDisabled}
                  error={!!evnErrorMsg || showRequired}
                  helperText={
                    evnErrorMsg
                      || (showRequired ? 'EVN is required' : '')
                      || (!evnDisabled && !isExistingUnit ? 'New unit entered — EVN options hidden' : '')
                  }
                  sx={styles.errorOutlined}
                />
              );
            }}
          />
        </Box>
      </Grid>

      {/* Meter Number */}
      <Grid item xs={12} sm={6} md={3}>
        <FieldLabel>Meter Number</FieldLabel>
        <Box sx={styles.fieldInput}>
          <Autocomplete
            freeSolo
            options={meterDisabled ? [] : []}
            filterOptions={(x) => x}
            value={meterNumber || ''}
            inputValue={meterInput}
            onInputChange={(_, v) => {
              setMeterInput(v || '');
              // commit immediately so meter type enables while tabbing
              setMeterNumber((v || '').trim());
            }}
            onChange={(_, v) => {
              const next = (typeof v === 'string' ? v : '') || '';
              setMeterInput(next);
              setMeterNumber(next.trim());
            }}
            onBlur={() => setMeterNumber((meterInput || '').trim())}
            forcePopupIcon={false}
            open={false}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
            disabled={meterDisabled}
            renderInput={(params) => {
              const meterError = METER_NUMBER_RULES.find((r) => !meterDisabled && (meterNumber || '') !== '' && !r.test(meterNumber || ''));
              const externalMeterError = formErrors.meterNumber || '';
              const meterErrorMsg = externalMeterError || (meterError ? meterError.message : '');
              const showRequired = submitted && !meterDisabled && isEmpty(meterNumber);
              return (
                <TextField
                  {...params}
                  placeholder={meterDisabled ? 'Enter or select EVN first' : 'Enter a new meter number'}
                  aria-label="Meter Number"
                  size="small"
                  variant="outlined"
                  fullWidth
                  required={!meterDisabled}
                  error={!!meterErrorMsg || showRequired}
                  helperText={meterErrorMsg || (showRequired ? 'Meter number is required' : '')}
                  sx={styles.errorOutlined}
                />
              );
            }}
          />
        </Box>
      </Grid>

      {/* Meter Type */}
      <Grid item xs={12} sm={6} md={3}>
        <FieldLabel>Meter Type</FieldLabel>
        <Box sx={styles.fieldInput}>
          <Autocomplete
            options={meterTypeOptions}
            value={meterType || null}
            onChange={(_, v) => setMeterType(v || null)}
            isOptionEqualToValue={(o, v) => o?.value === v?.value}
            getOptionLabel={(o) => o?.label || ''}
            disabled={meterTypeDisabled}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={meterTypeDisabled ? 'Enter meter number first' : 'Select meter type'}
                aria-label="Meter Type"
                size="small"
                variant="outlined"
                fullWidth
                required={!meterTypeDisabled}
                error={
                  !!formErrors.meterType ||
                  (submitted && !meterTypeDisabled && !meterType)
                }
                helperText={
                  formErrors.meterType ||
                  (submitted && !meterTypeDisabled && !meterType ? 'Meter type is required' : '')
                }
                sx={styles.errorOutlined}
              />
            )}
          />
        </Box>
      </Grid>

      {/* From Date */}
      <Grid item xs={12} sm={6} md={3}>
        <FieldLabel>From Date</FieldLabel>
        <Box sx={styles.fieldInput}>
          <ValidatedField
            type="date"
            aria-label="From Date"
            value={fromDate || ''}
            onChange={(e) => setFromDate(e.target.value)}
            rules={FROM_DATE_RULES}
            externalError={formErrors.fromDate || ''}
            sx={{
              ...styles.dateInput,
              ...styles.dateOutlinedInput,
              '& .MuiInputBase-input': {
                color: fromDate ? '#000' : '#757575',
              },
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Grid>

      {/* Comments */}
      <Grid item xs={12} sm={12} md={3}>
        <FieldLabel>Comments</FieldLabel>
        <Box sx={styles.fieldInput}>
          <ValidatedField
            placeholder="Add any comments (optional)"
            aria-label="Comments"
            value={comments || ''}
            onChange={(e) => setComments(e.target.value)}
            rules={COMMENTS_RULES}
            externalError={formErrors.comments || ''}
            multiline
            minRows={1}
          />
        </Box>
      </Grid>

      {/* Actions */}
      <Grid item xs={12} sm={12} md={3}>
        <FieldLabel>Actions</FieldLabel>
        <Box sx={{ ...styles.fieldInput, ...styles.actionsBox }}>
          {editing ? (
            <>
              <Button variant="outlined" sx={styles.buttonBase} onClick={onCancel}>Cancel</Button>
              <Button variant="contained" sx={{ ...styles.buttonBase, ...styles.purpleButton }} onClick={onSave} disabled={!canSubmit}>
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" sx={{ ...styles.buttonBase, ...styles.purpleButton }} onClick={handleAdd} disabled={!canSubmit}>
                Add to List
              </Button>
              <Button variant="contained" sx={{ ...styles.buttonBase, ...styles.purpleButton }} onClick={handleClear}>
                Clear fields
              </Button>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ConfigForm;